// UpgradeModal — shown when a user tries to do something gated (e.g. Save)
// and they aren't entitled. Presents the three customer-facing options:
//   - Customer Pro monthly
//   - Single Design one-off
//   - (Trial hint if they still have time left)
//
// Artist-side paywall uses a different modal (coming in a later step) since
// the pitch and price are very different.

import React, { useState } from "react";
import toast from "react-hot-toast";
import { startCheckout } from "./api";
import { PLANS } from "./constants";

const MODAL_PLANS = [
  PLANS.customer_pro,
  PLANS.single_design,
];

export default function UpgradeModal({ open, onClose, trialDaysLeft = 0, reason = "save" }) {
  const [loadingPlan, setLoadingPlan] = useState(null);

  if (!open) return null;

  const stillInTrial = trialDaysLeft > 0;

  async function handleChoose(plan) {
    try {
      setLoadingPlan(plan);
      await startCheckout(plan);
      // startCheckout navigates away; if we're still here something failed.
    } catch (err) {
      console.error("[upgrade-modal] checkout failed", err);
      toast.error(err.message || "Could not open checkout");
      setLoadingPlan(null);
    }
  }

  const reasonCopy =
    reason === "save"
      ? "Save your design and keep tweaking it whenever you want."
      : "Unlock the full Tattune experience.";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.75)",
        display: "grid",
        placeItems: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          background: "#0b0b0b",
          border: "1px solid #262626",
          borderRadius: 16,
          padding: 24,
          color: "#eaeaea",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <h2 style={{ fontSize: 20, margin: 0, letterSpacing: "0.02em" }}>
              {stillInTrial ? "Trial + beyond" : "Pick how you want to keep going"}
            </h2>
            <p style={{ fontSize: 13, color: "#a8a8a8", margin: "6px 0 0" }}>
              {reasonCopy}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              color: "#888",
              border: "none",
              fontSize: 22,
              cursor: "pointer",
              padding: "4px 8px",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {stillInTrial && (
          <div
            style={{
              background: "rgba(201, 45, 45, 0.1)",
              border: "1px solid rgba(201, 45, 45, 0.35)",
              color: "#e8a8a8",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            You still have <strong>{trialDaysLeft}</strong> day{trialDaysLeft === 1 ? "" : "s"} of trial
            left. You can keep designing today without paying.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {MODAL_PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              loading={loadingPlan === plan.id}
              disabled={loadingPlan !== null && loadingPlan !== plan.id}
              onChoose={() => handleChoose(plan.id)}
            />
          ))}
        </div>

        <p style={{ fontSize: 11, color: "#707070", textAlign: "center", marginTop: 16 }}>
          Payments by Stripe. Cancel subscriptions anytime from your account page.
        </p>
      </div>
    </div>
  );
}

function PlanCard({ plan, loading, disabled, onChoose }) {
  return (
    <button
      type="button"
      onClick={onChoose}
      disabled={loading || disabled}
      style={{
        textAlign: "left",
        background: "#141414",
        border: "1px solid #2b2b2b",
        borderRadius: 12,
        padding: "14px 16px",
        cursor: loading || disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        color: "inherit",
        transition: "border-color 120ms",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{plan.name}</div>
        <div style={{ fontSize: 14, color: "#f5f5f5" }}>
          <strong>{plan.price}</strong>
          <span style={{ color: "#9d9d9d", fontSize: 12, marginLeft: 4 }}>{plan.cadence}</span>
        </div>
      </div>
      <ul style={{ fontSize: 12, color: "#c2c2c2", margin: "8px 0 0", paddingLeft: 16, lineHeight: 1.5 }}>
        {plan.features.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      {loading && (
        <div style={{ fontSize: 12, color: "#c92d2d", marginTop: 10 }}>Opening Stripe…</div>
      )}
    </button>
  );
}
